import React, {useEffect, useState} from 'react';
import clsx from 'clsx';
import moment from 'moment';
import validate from 'validate.js';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Checkbox from '@material-ui/core/Checkbox';
import CloseIcon from '@material-ui/icons/Close';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import {Theme} from '@material-ui/core/styles/createMuiTheme';
import {makeStyles} from '@material-ui/styles';
import {BookingData, getAgeAtEvent, getMemberAgeAtEvent, GuestData} from '../helperFunctions';
import {getHasError} from '../../../utils/formHelpers';
import {Group, TourRoleType, TourRoleTypes} from '../../../graphql/groups.datatypes';
import HelpTooltip from './HelpTooltip';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        minWidth: 278,
        // maxWidth: 278,
        borderRadius: 0,
        marginRight: 30,
        overflow: 'visible',
        position: 'relative',
        transformStyle: 'preserve-3d',
        margin: '20px 0',
        // '&:after': {
            // content: '""',
            // display: 'block',
            // width: '100%',
            // height: '100%',
            // position: 'absolute',
            // right: -5,
            // bottom: -5,
            // backgroundColor: theme.palette.secondary.main,
            // opacity: 0.2,
            // transform: 'translateZ(-1px)',
        // },

        '& label': {
            fontWeight: 'bold',
            fontSize: 17,
        },

        '& .MuiInput-root input': {
            color: theme.palette.secondary.main,

            '&::placeholder': {
                color: theme.palette.black
            }
        }
    },
    rootPlaceholder: {
        cursor: 'pointer',

        '&:after': {
            content: 'none'
        }
    },
    header: {
        backgroundColor: theme.palette.secondary.main,
        paddingTop: 14,
        paddingBottom: 14,
        paddingLeft: 20,
        paddingRight: 20,
        height: 50,

        '& .MuiCardHeader-title': {
            color: theme.palette.white,
        },

        '& .MuiCardHeader-action': {
            marginTop: 0,
            alignSelf: 'center'
        }
    },
    headerPlaceholder: {
        backgroundColor: '#696969'
    },
    closeButton: {
        color: theme.palette.white,
    },
    containerCheckboxes: {
        marginTop: theme.spacing(1.5)
    },
    checkbox: {
        padding: 6,
        marginLeft: -8,
        color: theme.palette.primary.main,

        '& .MuiSvgIcon-root': {
            fontSize: '1.4rem'
        }
    },
    labelCheckbox: {
        fontSize: 16
    },
    content: {
        '& > div': {
            marginTop: 16
        },

        '& > div:first-of-type': {
            marginTop: 0
        }
    },
    labelDate: {
        marginTop: theme.spacing(2),
        marginBottom: -theme.spacing(1.75),
    }
}));

interface Props {
    bookingData: BookingData;
    group: Group;
    guestData: GuestData;
    guestIndex: number;
    isPlaceholder?: boolean;
    onClick?: ((event?: React.MouseEvent) => void) | undefined;
    roomGuestCount: number;
    removeGuest: (data: GuestData) => void;
    setGuestData: (data: GuestData) => void;
    roomRef?: React.MutableRefObject<HTMLDivElement | null>;
    readonly?: boolean;
};

const GuestInfo = (props: Props) => {
    const {
        bookingData,
        group,
        guestData,
        guestIndex,
        isPlaceholder = false,
        roomGuestCount,
        onClick,
        removeGuest,
        setGuestData,
        roomRef,
        readonly = false
    } = props;
    const classes = useStyles();
    const currentYear = moment().year();
    const dobMoment = moment(new Date(guestData.dob));

    const handleFieldChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (readonly) return;

        let value: any = event.target.type === 'checkbox' ?
            (event.target as HTMLInputElement).checked :
            event.target.value;

        // @ts-ignore
        switch (typeof guestData[event.target.name]) {
            case 'number':
                value = Number(value);
                break;
            case 'boolean':
                value = Boolean(value);
                break;
        }

        setGuestData({
            ...guestData,
            [event.target.name]: value
        });
    };

    const handleDateChange = (date: number, month: number, year: number) => {
        if (readonly) return;

        const dob = moment(new Date(year, month, date));
        const family = group.tour && group.tour.tour_roles && group.tour.tour_roles.find(checkRole => checkRole.tour_role === TourRoleType.TOUR_ROLE_FAMILY_AND_FRIENDS);
        let tourRole = guestData.tour_role;

        const ageAtEvent = getAgeAtEvent(bookingData, dob);
        if(tourRole !== -1) {
            const currentRole = group.tour && group.tour.tour_roles && group.tour.tour_roles.find(checkRole => checkRole.tour_role === tourRole);
            if(currentRole) {
                if(ageAtEvent < currentRole.age_lower_bound || ageAtEvent > currentRole.age_upper_bound){
                    tourRole = -1;
                }
            }
        }
        if (family) {
            if (getAgeAtEvent(bookingData, dob) >= family.age_lower_bound && guestData.tour_role === -1) {
                tourRole = TourRoleType.TOUR_ROLE_FAMILY_AND_FRIENDS;
            }
            if (getAgeAtEvent(bookingData, dob) < family.age_lower_bound) {
                tourRole = -1;
            }
        }

        setGuestData({...guestData, dob: dob.format('YYYY/MM/DD'), tour_role: tourRole});
    }

    //2. Setup helper functions for form
    const [errors, setErrors] = useState({
        isValid: true,
        errors: {}
    });
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        first_name: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        last_name: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        dob: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        tour_role: {
            numericality: {
                greaterThan: -1,
            }
        }
    };

    useEffect(() => {
        if (!isPlaceholder) {
            const newErrors = validate(guestData, validateSchema);
            const isValid = !newErrors;

            setErrors(state => ({
                ...state,
                isValid,
                errors: newErrors || {}
            }));

            if (guestData.isValid !== isValid) {
                setGuestData({...guestData, isValid});
            }
        }
    }, [guestData, isPlaceholder]);

    const title = guestData.first_name.length > 0 ?
        [guestData.first_name, guestData.last_name].join(' ') :
        'Traveler';

    const allowRemove = !readonly && !isPlaceholder && (guestIndex > 0 || (guestIndex === 0 && roomGuestCount > 1));
    const removeButton = (
        <Tooltip title="Remove this Traveler" placement="top">
            <IconButton className={classes.closeButton} onClick={() => removeGuest(guestData)} size="small">
                <CloseIcon color="inherit" fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    const tourRoles = (group.tour && group.tour.tour_roles) || [];

    return (
        <Card ref={roomRef} className={clsx(classes.root, isPlaceholder ? classes.rootPlaceholder : null)} onClick={onClick}>
            <CardHeader
                action={allowRemove ? removeButton : null}
                className={clsx(classes.header, isPlaceholder ? classes.headerPlaceholder : null)}
                title={`${guestIndex + 1}. ${title}`}
            />
            <CardContent className={classes.content}>
                <TextField
                    autoFocus
                    fullWidth
                    label="First Name*"
                    placeholder="e.g. Lily"
                    name="first_name"
                    {...hasError('first_name')}
                    onChange={handleFieldChange}
                    value={guestData.first_name}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    disabled={isPlaceholder || readonly}
                />

                <TextField
                    fullWidth
                    label="Last Name*"
                    placeholder="Smith"
                    name="last_name"
                    {...hasError('last_name')}
                    onChange={handleFieldChange}
                    value={guestData.last_name}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    disabled={isPlaceholder || readonly}
                />

                <InputLabel shrink className={classes.labelDate}>Date of Birth*</InputLabel>
                <Grid container>
                    <Grid item xs={5}>
                        <TextField
                            fullWidth
                            name="month"
                            onChange={(event) => handleDateChange(
                                dobMoment.date(),
                                Number(event.target.value),
                                dobMoment.year()
                            )}
                            select
                            SelectProps={{native: true}}
                            value={dobMoment.month()}
                            disabled={isPlaceholder || readonly}
                        >
                            {moment.months().map((val, i) => (
                                <option key={i} value={i}>
                                    {val}
                                </option>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            fullWidth
                            name="day"
                            onChange={(event) => handleDateChange(
                                Number(event.target.value),
                                dobMoment.month(),
                                dobMoment.year()
                            )}
                            select
                            SelectProps={{native: true}}
                            value={dobMoment.date()}
                            disabled={isPlaceholder || readonly}
                        >
                            {[...Array(dobMoment.daysInMonth())].map((val, i) => (
                                <option key={i} value={i + 1}>
                                    {i + 1}
                                </option>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            name="year"
                            onChange={(event) => handleDateChange(
                                dobMoment.date(),
                                dobMoment.month(),
                                Number(event.target.value)
                            )}
                            select
                            SelectProps={{native: true}}
                            value={dobMoment.year()}
                            disabled={isPlaceholder || readonly}
                        >
                            {[...Array(100)].map((val, i) => (
                                <option key={i} value={currentYear - i}>
                                    {currentYear - i}
                                </option>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>

                <Grid container className={classes.containerCheckboxes}>
                    <Grid item >
                        <Grid
                            container
                            component="label"
                            alignItems="center"
                            justify="flex-start"
                        >
                            <Grid item>
                                <Checkbox
                                    name="room_leader"
                                    size="small"
                                    className={classes.checkbox}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setGuestData({
                                        ...guestData,
                                        primary_occupant: event.target.checked
                                    })}
                                    checked={guestData.primary_occupant}
                                    disabled={isPlaceholder || readonly}
                                />
                            </Grid>
                            <Grid item>
                                <Typography variant="body1" className={classes.labelCheckbox}>
                                    Room Leader
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item >
                        <HelpTooltip
                            disabled={isPlaceholder || readonly}
                            message="The room leader is the responsible adult for this room."
                        />
                    </Grid>

                    {tourRoles.map(role => {
                        const roleId = role.tour_role;
                        const type = TourRoleTypes[roleId];
                        const ageAtEvent = getMemberAgeAtEvent(bookingData, guestData);
                        const disabled = isPlaceholder || (
                            ageAtEvent < role.age_lower_bound ||
                            ageAtEvent > role.age_upper_bound
                        );
                        let warningMessage: string = '';

                        if (role.age_upper_bound! < 99) {
                            warningMessage = `To select this role you must be
                                between ${role.age_lower_bound} and
                                ${role.age_upper_bound} years old at the time
                                of the event.`
                        }

                        if (role.age_upper_bound! >= 99) {
                            warningMessage = `To select this role you must be
                                over ${role.age_lower_bound} years old at the
                                time of the event.`
                        }                        

                        const labeledCheckbox = (
                            <Grid
                                container
                                component="label"
                                alignItems="center"
                                justify="flex-start"
                            >
                                <Grid item>
                                    <Checkbox
                                        name="room_leader"
                                        size="small"
                                        className={classes.checkbox}
                                        onChange={(
                                            event: React.ChangeEvent<HTMLInputElement>
                                        ) => setGuestData({
                                            ...guestData,
                                            tour_role: event.target.checked ? roleId : -1
                                        })}
                                        checked={disabled ? false : guestData.tour_role === roleId}
                                        disabled={disabled || readonly}
                                    />
                                </Grid>
                                <Grid item>
                                    <Typography variant="body1" className={classes.labelCheckbox}>
                                        {type.name}
                                    </Typography>
                                </Grid>
                            </Grid>
                        );

                        return (
                            <React.Fragment key={roleId}>
                                {withDisabledTooltip(
                                    disabled && !isPlaceholder,
                                    warningMessage,
                                    labeledCheckbox
                                )}
                            </React.Fragment>
                        );
                    })}
                </Grid>
            </CardContent>
        </Card>
    )
};

const withDisabledTooltip = (
    isDisabled: boolean,
    message: string,
    node: React.ReactElement,
): React.ReactElement => {
    return isDisabled && message ? <Tooltip title={message}>{node}</Tooltip> : node;
};

export default GuestInfo;
